import { useQuery } from '@tanstack/react-query';
import { ChevronsUpDown } from 'lucide-react';
import * as React from 'react';
import { diContainer } from 'src/dependencyInjection/diContainer';
import { DI_TYPES } from 'src/dependencyInjection/diTypes';
import { IHttpService } from 'src/services/HttpService/IHttpService';
import { Button } from 'src/shadcn/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from 'src/shadcn/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shadcn/components/ui/popover';
import { z } from 'zod';
import TrieSearch from 'trie-search';
import { FixedSizeList as ReactWindowList } from 'react-window';
import { CommandList } from 'cmdk';
import { vehicle_makes_and_models } from './makes_and_models';

export interface ICarMakeSelectorProps {
  value: string;
  setValue: (newValue: string) => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export const CarMakeSelector: React.FunctionComponent<
  ICarMakeSelectorProps
> = props => {
  const { value, setValue } = props;
  const [searchVal, setSearchVal] = React.useState<string>();
  const [open, setOpen] = React.useState(false);
  // const { makes, isLoading, error } = useGetCarMakes();
  const makes = React.useMemo(() => {
    return vehicle_makes_and_models.results.map(make => {
      return {
        Make_ID: make.make.value,
        Make_Name: make.make.label,
      };
    });
  }, []);
  const trie = React.useMemo(() => {
    const trie = new TrieSearch<z.infer<typeof makeRowValidator>>('Make_Name', {
      min: 1,
    });
    trie.addAll(makes || []);
    return trie;
  }, [makes]);

  const items =
    (searchVal?.length || 0) > 1 ? trie.search(searchVal || '') : makes;
  const isCreateNewEnabled = Boolean(
    searchVal?.length && searchVal.length > 1 && items?.length !== 1
  );
  const triggerText = value ? value : 'Select make...';
  return (
    <Popover
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) {
          setSearchVal(undefined);
        }
        setOpen(isOpen);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className='w-full justify-between'
          data-testid='car-make-selector-combobox'
        >
          <div className='truncate' title={triggerText}>
            {triggerText}
          </div>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-[250px] p-0'
        container={props.containerRef?.current}
      >
        <Command shouldFilter={false} className=''>
          <CommandInput
            placeholder='Search makes...'
            onValueChange={newValue => {
              setSearchVal(newValue);
            }}
            value={searchVal}
          />
          <CommandList>
            <CommandEmpty>No framework found.</CommandEmpty>
            {isCreateNewEnabled && (
              <CommandGroup heading='Create new' className='p-0'>
                <CommandItem
                  key={searchVal}
                  value={searchVal}
                  onSelect={currentValue => {
                    setOpen(false);
                    setSearchVal(undefined);
                    setValue((searchVal || '').trim());
                  }}
                  title={`Create item: ${searchVal}`}
                >
                  <span className='overflow-hidden text-ellipsis whitespace-nowrap'>
                    {`Create item: ${searchVal}`}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
            {items && Boolean(items?.length) && (
              <CommandGroup heading='Select exisitng' className='p-0'>
                <ReactWindowList
                  height={Math.min(
                    isCreateNewEnabled ? 204 : 264,
                    35 * items.length
                  )}
                  itemCount={items.length}
                  itemData={items.map(item => {
                    return {
                      item,
                      onSelect: () => {
                        setValue(item.Make_Name);
                        setOpen(false);
                        setSearchVal(undefined);
                      },
                    };
                  })}
                  itemSize={35}
                  width={250}
                >
                  {Row}
                </ReactWindowList>
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

function useGetCarMakes() {
  const { data, error, isLoading } = useQuery({
    queryKey: ['car-makes'],
    queryFn: async () => {
      const url = `https://vpic.nhtsa.dot.gov/api/vehicles/GetAllMakes?format=json`;
      const response = await diContainer
        .get<IHttpService>(DI_TYPES.HttpService)
        .send({
          type: 'GET',
          url,
          response: z.object({
            Count: z.number(),
            Message: z.string(),
            SearchCriteria: z.string().nullish(),
            Results: z.array(makeRowValidator),
          }),
        });
      return response;
    },
  });
  return {
    makes: data?.Results,
    error,
    isLoading,
  };
}

const makeRowValidator = z.object({
  // Make_ID: z.number(),
  Make_ID: z.string(),
  Make_Name: z.string(),
});

interface RowProps {
  style: React.CSSProperties;
  index: number;
  data: {
    item: z.infer<typeof makeRowValidator>;
    onSelect: () => void;
  }[];
}
const Row = React.forwardRef<HTMLDivElement, RowProps>(
  ({ style, data, index }, ref) => {
    const item = data[index].item;
    return (
      <CommandItem
        ref={ref}
        style={style}
        key={item.Make_ID}
        value={item.Make_Name}
        onSelect={data[index].onSelect}
        title={item.Make_Name}
      >
        <span className='overflow-hidden text-ellipsis whitespace-nowrap'>
          {item.Make_Name}
        </span>
      </CommandItem>
    );
  }
);
